import { Suspense, useEffect } from 'react';
import { useTranslation } from '@/locales';

import { useDashboardTableQuery } from '@/views/DashboardView/components/EntitiesSection/components/EntitiesTable/utils/useDashboardTableQuery';
import { useAlertsCountQuery } from '@/views/DashboardView/utils/useAlertsCountQuery';
import { IDashboardTourStep, useDashboardTour } from '@/providers/DashboardTourContext';
import { useNewFeature } from '@/providers/NewFeatureContextProvider/NewFeatureContext.hooks';
import { useBanners } from '@/providers/BannerProvider/BannerContext.hooks';
import { lazyWithReload } from '@/lib/chunkRecovery';
import { Banner } from '@/components/Banner/Banner';
import { StyledPageLoader } from '@/components/StyledSharedComponents/StyledLoader';
import ErrorBoundary from '@/components/Error/ErrorBoundary/ErrorBoundary';
import { CommonError } from '@/components/Error/CommonError/CommonError';
import { CentredCustomText } from '@/components/CentredCustomText/CentredCustomText';
import {
  StyledAgioH1,
  StyledAgioH5,
  StyledNormalText6,
} from '@/components/StyledSharedComponents/StyledTypography';
import { StyledViewContainer } from '@/views/DashboardView/DashboardView.styled';
import { ELocalStorage } from '@/types/storage';

const EntitiesTable = lazyWithReload(() =>
  import('@/views/DashboardView/components/EntitiesSection/components/EntitiesTable/EntitiesTable').then(
    module => ({ default: module.EntitiesTable }),
  ),
);
const BetaTranscendTable = lazyWithReload(() =>
  import('@/views/DashboardView/components/EntitiesSection/BetaTranscendTable').then(module => ({
    default: module.BetaTranscendTable,
  })),
);

const STABLECOINS_TOUR_STEP: IDashboardTourStep = {
  target: '[data-testid="stablecoins-navigation-button"]',
  content: (
    <>
      <StyledAgioH5>Stablecoins</StyledAgioH5>
      <StyledNormalText6>
        New! Reserves modelling and stress testing for insight on stablecoin issuer risk.
      </StyledNormalText6>
    </>
  ),
};

// Full onboarding flow shown to brand-new users (or anyone with a cleared/fresh browser).
const FULL_TOUR_STEPS: IDashboardTourStep[] = [
  {
    target: '[data-testid="dashboard-header-title"]',
    content: (
      <>
        <StyledAgioH5>What Is Risk Monitor</StyledAgioH5>
        <StyledNormalText6>
          Risk Monitor provides a unified view of ratings, monitoring signals, and on-chain activity
          for quick access to key risk insights.
        </StyledNormalText6>
      </>
    ),
  },
  {
    target: '[data-testid="dashboard-customize-button"]',
    content: (
      <>
        <StyledAgioH5>Customize Your Risk Monitor</StyledAgioH5>
        <StyledNormalText6>
          Use the Customize button to adjust your Risk Monitor alert settings and select the
          institutions that matter most to you.
        </StyledNormalText6>
      </>
    ),
  },
  {
    target: '[data-testid="dashboard-ratings-button"]',
    content: (
      <>
        <StyledAgioH5>View All Ratings</StyledAgioH5>
        <StyledNormalText6>
          Click Ratings to access the full list of rated entities.
        </StyledNormalText6>
      </>
    ),
  },
  {
    target: '[data-testid="dashboard-portfolio-table"]',
    content: (
      <>
        <StyledAgioH5>Drill Into Entity Details</StyledAgioH5>
        <StyledNormalText6>
          Click any row in the table to view detailed information for that entity.
        </StyledNormalText6>
      </>
    ),
  },
  STABLECOINS_TOUR_STEP,
  {
    target: '[data-testid="settings-navigation-button"]',
    content: (
      <>
        <StyledAgioH5>Settings</StyledAgioH5>
        <StyledNormalText6>
          Use the Settings icon to manage API keys and configure Slack or Telegram integrations.
        </StyledNormalText6>
      </>
    ),
  },
];

// Standalone flow shown to users who already completed the old 5-step flow,
// just to introduce the new stablecoins feature.
const STABLECOINS_ONLY_TOUR_STEPS: IDashboardTourStep[] = [STABLECOINS_TOUR_STEP];

export const DashboardView = () => {
  const { t } = useTranslation('dashboard');
  const { rows, isDataReady, isLoading, error, serverSideControls } = useDashboardTableQuery();

  // fired here, not only in the lazy table, so it runs alongside the table query
  // instead of waiting for the EntitiesTable chunk - the badge reads it from cache
  useAlertsCountQuery();

  const { isBetaUserFeature } = useNewFeature();

  const { bannersOffset } = useBanners();

  const { setSteps, setTourActive } = useDashboardTour();

  useEffect(() => {
    if (!isDataReady) return;

    // v2 is set once a user has seen the stablecoins feature (via either the full
    // flow or the standalone step), so its presence disables every tour variant.
    const sawCurrentTour = localStorage.getItem(ELocalStorage.DASHBOARD_TOUR_V2);
    if (sawCurrentTour) return;

    // Legacy flag => user already completed the old 5-step flow, so only introduce
    // stablecoins. Otherwise (new user, cleared storage, or different browser) shows
    // the full flow.
    const sawLegacyTour = localStorage.getItem(ELocalStorage.DASHBOARD_TOUR);

    setSteps(sawLegacyTour ? STABLECOINS_ONLY_TOUR_STEPS : FULL_TOUR_STEPS);
    setTourActive(true);
  }, [isDataReady, setSteps, setTourActive]);

  useEffect(() => {
    document.title = `Agio Ratings - Risk Monitor`;
  }, []);

  if (error) {
    throw error;
  }

  if (!isLoading && !isDataReady) {
    return <CentredCustomText text={t('empty-state')} />;
  }
  return (
    <StyledViewContainer bannersOffset={bannersOffset}>
      <Banner id="etf-update" variant="info" center>
        <StyledNormalText6>{t('etf-update-banner')}</StyledNormalText6>
      </Banner>

      <Suspense fallback={<StyledPageLoader />}>
        <EntitiesTable
          entityData={rows}
          serverSideControls={serverSideControls}
          isInitialLoading={isLoading}
        />

        {isBetaUserFeature && (
          <>
            <StyledAgioH1 color="primary">Transcend Data: </StyledAgioH1>
            <ErrorBoundary fallback={<CommonError variant="reload" />}>
              <BetaTranscendTable />
            </ErrorBoundary>
          </>
        )}
      </Suspense>
    </StyledViewContainer>
  );
};
